import { auth } from '@/lib/firebase';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

export interface AdminInstance {
  // Core fields (always present)
  user_id: string;
  created_by: string;
  tier: string;
  status: string;
  url: string;
  created_at: string;
  // Optional metadata fields
  user_tier?: string;
  pod_name?: string;
  pod_uid?: string;
  namespace?: string;
  node_name?: string | null;
  pod_phase?: string;
  build_version?: string;
  container_restarts?: number;
  uptime_seconds?: number | null;
  // Resource allocations
  memory_request?: string;
  memory_limit?: string;
  cpu_request?: string;
  cpu_limit?: string;
  gpu_request?: string;
  // User information (enriched in frontend)
  user_email?: string | null;
  user_display_name?: string | null;
}

interface AdminInstancesData {
  instances: AdminInstance[];
  total: number;
}

export const useAdminInstances = () => {
  const [instances, setInstances] = useState<AdminInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstances = useCallback(async (): Promise<void> => {
    const user = auth.currentUser;

    // Set loading state
    setLoading(true);

    // Clear error state
    setError(null);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken = await user.getIdToken();
      const response = await fetch('/api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ action: 'list' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch instances: ${response.status}`);
      }

      const result = await response.json();
      const data: AdminInstancesData = result.data;

      // Update instances data
      setInstances(data.instances || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to fetch instances: ${errorMessage}`);
      setError(errorMessage);
    } finally {
      // Clear loading state
      setLoading(false);
    }
  }, []);

  return {
    instances,
    loading,
    error,
    fetchInstances,
  };
};
